import { APPOINTMENT_KEY_RE, normalizeTime } from '@ehr/shared';

// ehr#663: the time helpers moved to @ehr/shared with the sign-off checks.
export { normalizeTime, to24h } from '@ehr/shared';

/**
 * calendar#46 step 6: the calendar's "Progress Note" button opens
 *   /?contact=<id>&view=clinical_notes&appt=<eventId>@<YYYY-MM-DD>
 *     &appt_time=<9:00 AM>&appt_type=<type or calendar name>&appt_dur=<minutes>
 * The Clinical Notes tab reads it once: it opens that appointment's note if one exists,
 * otherwise a new note pre-filled from the appointment. The params are then cleared so
 * a refresh or closing the drawer does not start over.
 */

export interface ApptPrefill {
  appointmentKey: string;
  /** YYYY-MM-DD, from the key. */
  date: string;
  /** "9:00 AM", or '' when the link had none. */
  time: string;
  /** One of the editor's session types. */
  sessionType: string;
  /** Minutes, or null when the link had none. */
  durationMin: number | null;
  /** ehr#266: the appointment's own type name as the Calendar sent it ('' when none). */
  typeLabel?: string;
  /** ehr#437: where it was held, from an appointment picked in "+ Add new". */
  place?: 'In person' | 'Telehealth';
  /**
   * calendar#139: a Family or Couples appointment opens the Family & Couples note page. From the
   * Calendar's &appt_kind=family|couples (its Family / Couples Session pills), else the type name.
   */
  familyType?: 'Family' | 'Couples';
}

const PARAMS = ['appt', 'appt_time', 'appt_type', 'appt_dur', 'appt_kind'] as const;

/** calendar#139: appt_kind first; an older appointment's "Family Therapy" / "Couples Therapy" name. */
export function familyTypeFrom(
  kind: string | null,
  sessionType: string,
): 'Family' | 'Couples' | undefined {
  const k = (kind ?? '').trim().toLowerCase();
  if (k === 'family') return 'Family';
  if (k === 'couples') return 'Couples';
  if (k) return undefined; // another kind the Calendar sent: not a family session
  return sessionType === 'Family' || sessionType === 'Couples' ? sessionType : undefined;
}

/** Calendar type / calendar name -> the editor's session type. */
export function sessionTypeFrom(label: string): string {
  const s = label.toLowerCase();
  if (/crisis/.test(s)) return 'Crisis'; // ehr#437: "Crisis Intervention", "Crisis Assessment"
  if (/intake|assessment|evaluation|new client/.test(s)) return 'Intake'; // ehr#527
  if (/couple|marital|marriage/.test(s)) return 'Couples';
  if (/family/.test(s)) return 'Family';
  if (/group/.test(s)) return 'Group';
  return 'Individual';
}

/**
 * Parse the appointment params from a query string; null when absent or malformed.
 * ehr#622: with a contact id, null too when the link names another client (?contact=): a session
 * link left in the URL after that client's chart didn't open must not pre-fill a note for the
 * next client opened.
 */
export function parseApptParams(search: string, contactId?: string): ApptPrefill | null {
  const p = new URLSearchParams(search);
  const key = p.get('appt');
  if (!key || !APPOINTMENT_KEY_RE.test(key)) return null;
  const linked = p.get('contact');
  if (contactId && linked && linked !== contactId) return null;
  const dur = Number(p.get('appt_dur'));
  const sessionType = sessionTypeFrom(p.get('appt_type') ?? '');
  const familyType = familyTypeFrom(p.get('appt_kind'), sessionType);
  return {
    appointmentKey: key,
    date: key.slice(key.lastIndexOf('@') + 1),
    time: normalizeTime(p.get('appt_time') ?? ''),
    sessionType,
    typeLabel: (p.get('appt_type') ?? '').trim(),
    durationMin: Number.isInteger(dur) && dur > 0 && dur <= 600 ? dur : null,
    ...(familyType ? { familyType } : {}),
  };
}

export function readApptParam(contactId?: string): ApptPrefill | null {
  return parseApptParams(window.location.search, contactId);
}

/**
 * Set the appointment params before opening the client's Clinical Notes (reminders#2: the
 * Progress Notes page's To write rows; the same link as the Calendar's Progress Note button).
 */
export function setApptParams(params: Record<string, string>): void {
  const url = new URL(window.location.href);
  for (const k of PARAMS) url.searchParams.delete(k);
  for (const [k, v] of Object.entries(params))
    if ((PARAMS as readonly string[]).includes(k)) url.searchParams.set(k, v);
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
}

/** Drop the appointment params from the URL (the contact + view params stay). */
export function clearApptParams(): void {
  const url = new URL(window.location.href);
  if (!PARAMS.some((k) => url.searchParams.has(k))) return;
  for (const k of PARAMS) url.searchParams.delete(k);
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
}

/**
 * calendar#46 step 8: the calendar's "Add group note" opens
 *   /?group=<eventId>@<YYYY-MM-DD>[&focus=<contactId>]
 * (focus = the attendee to open first; used when a group note is opened from a
 * client's Clinical Notes tab).
 */
export function readGroupParam(): { appointmentKey: string; focus: string | null } | null {
  const p = new URLSearchParams(window.location.search);
  const key = p.get('group');
  if (!key || !APPOINTMENT_KEY_RE.test(key)) return null;
  return { appointmentKey: key, focus: p.get('focus') };
}

export function clearGroupParams(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('group');
  url.searchParams.delete('focus');
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
}

/**
 * calendar#46 step 9: open one progress note in the client's Clinical Notes,
 *   /?contact=<id>&view=clinical_notes&note=<noteId>
 * (from the Progress Notes queue). Read once, then cleared. NEW_NOTE instead of an id
 * starts a new note (ehr#181, the page's + New note; ehr#479: via the appointment and kind pickers).
 */
export const NEW_NOTE = 'new';
export function setNoteParam(noteId: string): void {
  const url = new URL(window.location.href);
  url.searchParams.set('note', noteId);
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
}
export function readNoteParam(): string | null {
  return new URLSearchParams(window.location.search).get('note');
}
/**
 * ehr#682: while a copy of a note is the one being edited, the URL names it (and the chart's
 * Clinical Notes tab), so reloading that tab reopens the note and keeps it. Returns the undo,
 * which puts the URL back as it was (only if it still names this note).
 */
export function holdNoteInUrl(contactId: string, noteId: string): () => void {
  const url = new URL(window.location.href);
  if (url.searchParams.get('contact') !== contactId) return () => undefined;
  const prevView = url.searchParams.get('view');
  url.searchParams.set('view', 'clinical_notes');
  url.searchParams.set('note', noteId);
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  return () => {
    const u = new URL(window.location.href);
    if (u.searchParams.get('note') !== noteId) return;
    u.searchParams.delete('note');
    if (
      u.searchParams.get('contact') === contactId &&
      u.searchParams.get('view') === 'clinical_notes'
    ) {
      if (prevView) u.searchParams.set('view', prevView);
      else u.searchParams.delete('view');
    }
    window.history.replaceState(window.history.state, '', u.pathname + u.search + u.hash);
  };
}
export function clearNoteParam(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('note')) return;
  url.searchParams.delete('note');
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
}

/** The Calendar module. */
export const CALENDAR_BASE = 'https://calendar.credifyfast.com';

/**
 * ehr#317: open one appointment in the Calendar, ?appt=<eventId>[@<YYYY-MM-DD>] (the Calendar's
 * own deep link). A group session's row id in the chart and on the Dashboard is
 * "<eventId>:<dateKey>" (one occurrence); a 1:1 row's id is the event id.
 */
export function calendarApptHref(rowId: string): string {
  const at = rowId.lastIndexOf(':');
  const key = at > 0 ? `${rowId.slice(0, at)}@${rowId.slice(at + 1)}` : rowId;
  return `${CALENDAR_BASE}/?appt=${encodeURIComponent(key)}`;
}

/** ehr#317: the Calendar's New appointment with this client filled in (Calendar V218). */
export function calendarNewApptHref(contactId: string): string {
  return `${CALENDAR_BASE}/?newAppt=${encodeURIComponent(contactId)}`;
}

/**
 * ehr#909: the Appointments tab with none yet. It points to Schedule appointment only when the
 * button is there.
 */
export function apptEmptyText(canSchedule: boolean): string {
  return canSchedule
    ? 'No appointments yet. Use Schedule appointment to add one.'
    : 'No appointments yet.';
}
